import { useLazyQuery, useMutation } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  Divider,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import {
  InvoiceArrival,
  Product as ProductEntity,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useFormContext } from "react-hook-form";
import { Colors } from "react-native-ui-lib";
import { InvoiceAddProductModalRootScreenParamList } from "../../../../types";
import Product from "../../../../utilities/data/model/Product";
import { useTranslation } from "../../../../utilities/i18n";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { ProductSummaryCard } from "../../../products/components";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { QUERY_PRODUCT } from "../../../products/utils";
import { InvoiceArrivalItemForm } from "../../components/InvoiceArrivalItemForm";
import { InvoiceArrivalItemFormValues } from "../../components/InvoiceArrivalItemForm/types";
import { MUTATION_UPDATE_INVOICE_ARRIVAL_LINE_ITEMS } from "../../utils";
import { useInvoiceArrivalContextProvider } from "../../utils/active-invoice-arrival";
import { useSelectedProductContextProvider } from "../../utils/selected-product/hooks";
import {
  invoiceLineItemDataToGraphqlInput,
  itemToFormInput,
} from "../../utils/utils";

export type AddProductModalScreenProps =
  StackScreenProps<InvoiceAddProductModalRootScreenParamList>;

const AddProduct = ({
  product,
  navigation,
}: AddProductModalScreenProps & {
  product: Product;
}) => {
  const { t } = useTranslation("invoice_arrival");

  const { handleSubmit, formState, setError, trigger } =
    useFormContext<InvoiceArrivalItemFormValues>();

  const [m] = useMutation<{ updateInvoiceArrivalLineItems: InvoiceArrival }>(
    MUTATION_UPDATE_INVOICE_ARRIVAL_LINE_ITEMS
  );

  const {
    errors,
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
  } = formState;

  const flashMessagesContext = useFlashMessages();

  React.useEffect(() => {
    // to reset the form if the api returns error
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  const { invoiceArrival, setInvoiceArrival } =
    useInvoiceArrivalContextProvider();

  const handleOnSubmit = React.useCallback(
    async (input: InvoiceArrivalItemFormValues) => {
      try {
        const result = await m({
          variables: {
            id: invoiceArrival.id,
            itemsToCreate: [
              {
                ...invoiceLineItemDataToGraphqlInput(input),
              },
            ],
          },
        });

        if (result.data?.updateInvoiceArrivalLineItems) {
          setInvoiceArrival(result.data?.updateInvoiceArrivalLineItems);
          navigation.getParent()?.goBack();
        } else {
          throw new Error(t("common:error"));
        }
      } catch (err: any) {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
        setError("root.serverError", {
          type: "400",
          message: t("common:error"),
        });
      }
    },
    [invoiceArrival.id]
  );

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.add_product")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page footer={footerMarkup}>
      {product.id && (
        <ProductSummaryCard
          productId={product.id}
          locationId={invoiceArrival.locationId}
          onPress={() => {
            navigation.navigate("EditProductRootScreen", {
              productId: product.id,
              screen: "EditProductScreen",
            });
          }}
        />
      )}
      <Divider />
      <InvoiceArrivalItemForm />
    </Page>
  );
};

export const AddProductModalScreen = (props: AddProductModalScreenProps) => {
  const { product: productModel } = useSelectedProductContextProvider();

  const [productEntity, setProductEntity] = React.useState<
    ProductEntity | undefined
  >(undefined);

  const { invoiceArrival } = useInvoiceArrivalContextProvider();
  const [q] = useLazyQuery<{ product: ProductEntity }>(QUERY_PRODUCT);
  const transitionEnded = useHasTransitionEnded(props.navigation);

  React.useEffect(() => {
    if (productModel?.id) {
      q({
        variables: {
          id: productModel.id,
        },
      })
        .then((result) => {
          setProductEntity(result.data?.product);
        })
        .catch((err) => console.log(err));
    }
  }, [productModel?.id]);

  if (false == transitionEnded || !productModel || !productEntity) {
    return (
      <PlaceholderLoadingScreen
        navigation={props.navigation}
        modifyHeader={false}
        content={ResourcePageSkeleton}
        headerShown={false}
      />
    );
  }

  return (
    <InvoiceArrivalItemForm.Provider
      defaultFormValues={productToFormValues(invoiceArrival, productEntity)}
    >
      <AddProduct product={productModel} {...props} />
    </InvoiceArrivalItemForm.Provider>
  );
};

const productToFormValues = (
  invoiceArrival: InvoiceArrival,
  product: ProductEntity
): Partial<InvoiceArrivalItemFormValues> => {
  const pricing = product.locationInventoryPricing.find(
    (pricing) => pricing.locationId === invoiceArrival.locationId
  );
  return itemToFormInput({
    product: product,
    barcode: product.stockNumber ?? "",
    costPrice: pricing?.costPrice ?? 0,
    retailPrice: pricing?.retailPrice ?? 0,
    webPrice: pricing?.retailPrice ?? 0,
  });
};
